import {
  safeSessionStorageGetItem,
  safeSessionStorageSetItem,
} from '@onyx/client-storage';
import { isDefined } from '@onyx/utils/typescript';
import omit from 'lodash/omit';

import type {
  SetEventProperties,
  SetPageProperty,
  SetSessionId,
  SetSessionProperty,
  SetUserId,
} from '../../interface/properties';

const STORAGE_KEY = 'onyx-event-data';
const SESSION_ID_KEY = 'ms_session_id';
const USER_ID_KEY = 'ms_user_id';

export const pageProperties = new Map<string, unknown>();
export const sessionProperties = new Map<string, unknown>();

export const setPageProperty: SetPageProperty = (
  key: string,
  value: unknown
) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (!isDefined(value)) {
    pageProperties.delete(key);
  } else {
    pageProperties.set(key, value);
  }
};

export const setSessionProperty: SetSessionProperty = (
  key: string,
  value: unknown
) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (!isDefined(value)) {
    sessionProperties.delete(key);
  } else {
    sessionProperties.set(key, value);
  }
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === undefined
  ) {
    window.dynatrace?.sendSessionPropertyEvent?.({
      [`session_properties.${key}`]: value,
    });
  } else {
    window.dynatrace?.sendSessionPropertyEvent?.({
      [`session_properties.${key}`]: String(value),
    });
  }
  const [jsonStr] = safeSessionStorageGetItem(STORAGE_KEY);
  const existingData: Record<string, unknown> = JSON.parse(jsonStr || '{}');
  const updatedData = !isDefined(value)
    ? omit(existingData, [key])
    : { ...existingData, [key]: value };
  safeSessionStorageSetItem(STORAGE_KEY, JSON.stringify(updatedData));
};

export const setEventProperties: SetEventProperties = (eventProperties) => {
  if (typeof window === 'undefined') {
    return;
  }

  const prefixedEventProperties = Object.fromEntries(
    Object.entries(eventProperties).map(([key, value]) => [
      `event_properties.${key}`,
      value,
    ])
  );

  return window.dynatrace?.addEventModifier?.((event) => {
    if (typeof event !== 'object' || event === null || Array.isArray(event)) {
      return event;
    }

    return {
      ...event,
      ...prefixedEventProperties,
    };
  });
};

export const setSessionId: SetSessionId = (sessionId: string) => {
  setSessionProperty(SESSION_ID_KEY, sessionId);
};

export const setUserId: SetUserId = (userId: string) => {
  setSessionProperty(USER_ID_KEY, userId);
  window.dynatrace?.identifyUser?.(userId);
};

export const restoreSessionProperties = (): void => {
  const [jsonStr] = safeSessionStorageGetItem(STORAGE_KEY);
  const data: Record<string, unknown> = JSON.parse(jsonStr || '{}');
  Object.entries(data).forEach(([key, value]) => {
    sessionProperties.set(key, value);
  });
};

export const resetProperties = (): void => {
  pageProperties.clear();
  sessionProperties.clear();
};
